import React, { useState, useEffect } from 'react';
import { Search, Users, X, Check } from 'lucide-react';

const RecipientSelector = ({ contacts = [], onSelectionChange, preSelected = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    businessUnits: [],
    tags: [],
    status: 'active',
    hasEmail: true
  });
  const [selectedContacts, setSelectedContacts] = useState(preSelected);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Extract unique business units and tags from contacts
  const availableBusinessUnits = [...new Set(contacts.map(c => c.businessUnit).filter(Boolean))];
  const availableTags = [...new Set(contacts.flatMap(c => c.tags || []))];

  // Filter contacts based on all criteria
  const filteredContacts = contacts.filter(contact => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        contact.name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.phone?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Business unit filter
    if (filters.businessUnits.length > 0) {
      if (!filters.businessUnits.includes(contact.businessUnit)) return false;
    }

    // Tags filter
    if (filters.tags.length > 0) {
      if (!contact.tags || !filters.tags.some(tag => contact.tags.includes(tag))) {
        return false;
      }
    }

    // Status filter
    if (filters.status === 'active') {
      if (contact.status !== 'subscribed') return false;
    }

    // Has email filter
    if (filters.hasEmail && !contact.email) return false;

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      if (filterType === 'businessUnits' || filterType === 'tags') {
        const currentValues = prev[filterType];
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...prev, [filterType]: newValues };
      }
      return { ...prev, [filterType]: value };
    });
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle individual selection
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      const newSelection = prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId];
      
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }
      return newSelection;
    });
  };

  // Handle select all (applies current filters)
  const handleSelectAll = () => {
    const allFilteredIds = filteredContacts.map(c => c._id);
    setSelectedContacts(prev => {
      const allSelected = allFilteredIds.every(id => prev.includes(id));
      const newSelection = allSelected 
        ? prev.filter(id => !allFilteredIds.includes(id)) // Deselect all filtered
        : [...new Set([...prev, ...allFilteredIds])]; // Select all filtered
      
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }
      return newSelection;
    });
  };

  // Clear all selections
  const handleClearSelection = () => {
    setSelectedContacts([]);
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      businessUnits: [],
      tags: [],
      status: 'active',
      hasEmail: true
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    setSelectedContacts(preSelected);
  }, [preSelected]);

  const allFilteredSelected = filteredContacts.length > 0 && 
    filteredContacts.every(c => selectedContacts.includes(c._id));

  return (
    <div className="recipient-selector bg-white border border-gray-200 rounded-lg p-6 max-h-[70vh] flex flex-col overflow-hidden">
      {/* Header - Fixed */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Select Recipients
        </h2>
        {selectedContacts.length > 0 && (
          <button
            onClick={handleClearSelection}
            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <X className="w-4 h-4" />
            Clear Selection
          </button>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
        {/* Filters Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Filters:</h3>
          
          {/* Business Units */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Business Unit:
            </label>
            <div className="flex flex-wrap gap-2">
              {availableBusinessUnits.length > 0 ? (
                availableBusinessUnits.map(unit => (
                  <button
                    key={unit}
                    onClick={() => handleFilterChange('businessUnits', unit)}
                    className={`px-3 py-1 rounded-lg text-sm transition ${
                      filters.businessUnits.includes(unit)
                        ? 'bg-purple-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {unit}
                  </button>
                ))
              ) : (
                <span className="text-xs text-gray-500">No business units available</span>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Tags:
            </label>
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              {availableTags.length > 0 ? (
                availableTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleFilterChange('tags', tag)}
                    className={`px-3 py-1 rounded-lg text-xs transition ${
                      filters.tags.includes(tag)
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {tag}
                  </button>
                ))
              ) : (
                <span className="text-xs text-gray-500">No tags available</span>
              )}
            </div>
          </div>

          {/* Status & Email Filters */}
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={filters.status === 'active'}
                onChange={() => handleFilterChange('status', 'active')}
                className="rounded"
              />
              <span className="text-gray-700">Active Only</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={filters.status === 'all'}
                onChange={() => handleFilterChange('status', 'all')}
                className="rounded"
              />
              <span className="text-gray-700">All Status</span>
            </label>
            <label className="flex items-center gap-2 text-sm ml-auto">
              <input
                type="checkbox"
                checked={filters.hasEmail}
                onChange={(e) => handleFilterChange('hasEmail', e.target.checked)}
                className="rounded"
              />
              <span className="text-gray-700">Has Email (required)</span>
            </label>
          </div>

          {/* Clear Filters Button */}
          {(filters.businessUnits.length > 0 || filters.tags.length > 0 || searchTerm) && (
            <button
              onClick={handleClearFilters}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Clear All Filters
            </button>
          )}
        </div>

        {/* Search */}
        <div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="text-sm text-gray-600">
            Showing {filteredContacts.length} customer{filteredContacts.length !== 1 ? 's' : ''}
            {filteredContacts.length !== contacts.length && ` (filtered from ${contacts.length})`}
          </div>
          <div className="text-sm font-semibold text-blue-600">
            Selected: {selectedContacts.length} of {filteredContacts.length}
          </div>
        </div>

        {/* Select All */}
        {filteredContacts.length > 0 && (
          <div className="flex items-center gap-2 pb-3 border-b">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={handleSelectAll}
              className="rounded"
            />
            <label className="text-sm font-medium text-gray-700 cursor-pointer" onClick={handleSelectAll}>
              Select All ({filteredContacts.length})
            </label>
          </div>
        )}

        {/* Contacts List */}
        <div className="space-y-2">
          {paginatedContacts.length > 0 ? (
            paginatedContacts.map((contact) => (
              <div
                key={contact._id}
                onClick={() => handleSelectContact(contact._id)}
                className={`border rounded-lg p-3 cursor-pointer transition ${
                  selectedContacts.includes(contact._id)
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedContacts.includes(contact._id)}
                    onChange={() => handleSelectContact(contact._id)}
                    className="mt-1 rounded"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{contact.name}</span>
                      {selectedContacts.includes(contact._id) && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {contact.email || 'No email'}
                    </div>
                    {contact.phone && (
                      <div className="text-xs text-gray-500">{contact.phone}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {contact.businessUnit && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          {contact.businessUnit}
                        </span>
                      )}
                      {contact.tags && contact.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                      {contact.tags && contact.tags.length > 3 && (
                        <span className="text-xs text-gray-500">+{contact.tags.length - 3} more</span>
                      )}
                    </div>
                    {contact.loyaltyPoints !== undefined && (
                      <div className="text-xs text-gray-500 mt-1">
                        Points: {contact.loyaltyPoints}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No customers match your filters</p>
              <button
                onClick={handleClearFilters}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                Clear filters to see all customers
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientSelector;